import { useBusinessEntityDocuments } from '@commenda/api/business-entity';
import { updateTask } from '@commenda/api/service-request';
import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, Icons, Loading, useToggle } from '@commenda/kakashi';
import { documentName } from '@commenda/utils';
import {
  IncorporationRequestStatus,
  LegacyDocumentType,
  TaskParty,
  TaskStatus,
} from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { ConfirmationModal } from '../../../molecules/modals/ConfirmationModal';
import DocumentPreview from '../../common/document-preview';
import MultiDocumentPreview from '../../common/document-preview-multi';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { WorkflowProcessDrawer } from '../../common/workflow-process-drawer';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import {
  areDocumentsPresent,
  isDocumentPresent,
  updateTaskStatus,
} from '../../utils';
import { CaCorpIncorporationTask } from '../config';

export const UploadIncorporationDocuments = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest, mutate } = context;

  const { setNotification } = useNotification();

  // Fetch incorporation documents dynamically from Commenda OS
  // This is unusual - different provinces in Canada each have their own incorporation doc types.
  const businessEntityId = serviceRequest?.corporation?.id;

  const { data: docsData } = useBusinessEntityDocuments({ businessEntityId });

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );

  // Open questionnaire issue puts the whole SR in the legacy "client fixing" waiting state.
  // Own task is agent-side; the SR-wide wait keys on the fill/review questionnaire pair.
  const fillTask = serviceRequest?.taskGroups
    ?.flatMap((taskGroup) => taskGroup.tasks)
    ?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.FILL_CORPORATION_DETAILS
    );
  const reviewTask = serviceRequest?.taskGroups
    ?.flatMap((taskGroup) => taskGroup.tasks)
    ?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        CaCorpIncorporationTask.REVIEW_CORPORATION_DETAILS
    );
  const hasOpenReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    fillTask,
    reviewTask
  );

  // Map of documentSubType -> isUnique flag
  const formationDocs =
    docsData?.documents?.filter(
      (doc) => doc.documentType === 'FORMATION_DOCUMENT'
    ) || [];

  const documentsMeta: Partial<Record<LegacyDocumentType, boolean>> = {};
  formationDocs.forEach((doc) => {
    if (!(doc.documentSubType in documentsMeta)) {
      documentsMeta[doc.documentSubType] = doc.isUnique;
    }
  });

  const documentSubTypes = Object.keys(documentsMeta) as LegacyDocumentType[];

  const isTaskCompleted = areDocumentsPresent({
    documentTypes: documentSubTypes,
    serviceRequest,
  });

  const workflowProcessDrawerVisibility = useToggle();
  const confirmationModalVisibility = useToggle();

  // Build steps array (can be empty during loading)
  const steps = documentSubTypes.map((eachDocument) => {
    const isUnique = documentsMeta[eachDocument];
    const PreviewComponent = isUnique ? DocumentPreview : MultiDocumentPreview;
    return {
      heading: documentName(eachDocument),
      key: eachDocument,
      description: '',
      status: isDocumentPresent({
        documentType: eachDocument,
        serviceRequest,
      })
        ? IncorporationRequestStatus.Completed
        : IncorporationRequestStatus.NotStarted,
      children: (
        <PreviewComponent
          serviceRequest={serviceRequest}
          serviceRequestTaskId={task.id}
          mutate={mutate}
          documentType={eachDocument}
          viewOnly={task.pendingOn !== role}
          onUploadSuccess={async () => {
            if (task.status === TaskStatus.TO_DO) {
              const res = await updateTask({
                id: task.id,
                data: { status: TaskStatus.IN_PROGRESS },
              });
              if (!res.ok) {
                setNotification({
                  show: true,
                  valid: false,
                  title: 'Error',
                  message: 'Failed to update the task status.',
                });
              }
            }
          }}
        />
      ),
    };
  });

  // Backup step in case no documents are available in Commenda OS for this corporation type:
  if (docsData && docsData.count === 0 && !steps.length) {
    steps.push({
      heading: 'Documents not found',
      key: LegacyDocumentType.InvalidDefaultValue,
      description: '',
      status: IncorporationRequestStatus.Completed,
      children: (
        <p>
          We can&apos;t find what you&apos;re looking for. Please contact
          Customer Support at operations@commenda.io
        </p>
      ),
    });
  }

  const [activeParentStepId, setActiveParentStepId] = useState<string>(
    steps[0]?.key || ''
  );

  // Show loader while documents are loading
  if (businessEntityId && !docsData) {
    return (
      <div className="flex flex-col items-center justify-center h-60">
        <Loading />
      </div>
    );
  }

  const markTaskAsComplete = async () => {
    const res = await updateTaskStatus({
      task,
      status: TaskStatus.COMPLETED,
      mutate,
    });
    if (!res) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to update the task status.',
      });
    }
    workflowProcessDrawerVisibility.toggle();
    confirmationModalVisibility.hide();
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation documents uploaded by Commenda"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads incorporation documents" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel label="Commenda uploads incorporation documents" />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                View <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Incorporation documents uploaded"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.TO_DO]: {
            actions: (
              <Button
                size="sm"
                onClick={workflowProcessDrawerVisibility.show}
                variant="primary"
              >
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload incorporation documents" />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button
                size="sm"
                variant="secondary"
                onClick={workflowProcessDrawerVisibility.show}
              >
                Continue <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Upload incorporation documents"
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <Button size="sm" disabled onClick={() => {}} variant="primary">
                Start <Icons.ArrowNarrowRight className="size-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel label="Upload incorporation documents" />
            ),
          },
        },
      };

      if (
        hasOpenReportedIssue &&
        state.status !== TaskStatus.COMPLETED &&
        stateMap[state.role]
      ) {
        return {
          actions: null,
          label: (
            <ServiceRequestTaskLabel
              label="Client fixing the reported issues"
              status={task.status}
              timestamp={task.updatedAt}
            />
          ),
        };
      }

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <WorkflowProcessDrawer
        isShown={workflowProcessDrawerVisibility.visible}
        onClose={workflowProcessDrawerVisibility.hide}
        activeParentStepId={activeParentStepId}
        setActiveParentStepId={setActiveParentStepId}
        hideHeader
        header={
          <div className="w-full flex justify-between mr-4 items-center">
            <h3 className="txt-large-medium text-text-secondary ">
              Upload incorporation documents
            </h3>
            {isTaskCompleted &&
              task.status !== TaskStatus.COMPLETED &&
              task.pendingOn === role && (
                <Button
                  size="sm"
                  onClick={confirmationModalVisibility.show}
                  disabled={task.pendingOn !== role}
                >
                  <Icons.Check className="w-4" />
                  Mark as complete
                </Button>
              )}
          </div>
        }
        steps={steps}
      />
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
      <ConfirmationModal
        isOpen={confirmationModalVisibility.visible}
        onClose={confirmationModalVisibility.hide}
        onConfirm={markTaskAsComplete}
        title="Mark as complete"
        confirmButtonText="Continue"
        message="Please note that this action will update the customer. To revert this change, please contact Commenda support."
        hideCancelButton
      />
    </div>
  );
};
